import { useTranslate } from 'hooks/useTranslate';
import { Envelope, EnvelopeStatus } from 'types/Envelope';
import EnvelopeTimelineItem from './EnvelopeTimelineItem';
import './EnvelopeTimeline.scss';

type EnvelopeTimelineProps = {
  envelope: Envelope & { envelopeStatus?: EnvelopeStatus };
  completedSigners: number;
  progressPercentage: number;
  invertedDirection?: boolean;
  uniformMarkers?: boolean;
};

type TimelineItem = {
  step: 'created' | 'sent' | 'awaiting' | 'started' | 'completed';
  title: string;
  date: string | number | Date;
  description: string;
  isCompleted: boolean;
  isActive: boolean;
};

const EnvelopeTimeline = ({
  envelope,
  completedSigners,
  progressPercentage,
  invertedDirection = false,
  uniformMarkers = false,
}: EnvelopeTimelineProps) => {
  const { t } = useTranslate();

  const { envelopeStatus } = envelope;

  const timelineItems: TimelineItem[] = [
    {
      step: 'created',
      title: t('Envelope Created'),
      date: envelope.creationDate,
      description: t('Envelope was created and prepared for signing'),
      isCompleted: true,
      isActive: false,
    },
    {
      step: 'sent',
      title: t('Envelope Sent'),
      date: envelopeStatus?.envelopeInfo.sentDateTime || envelope.creationDate,
      description: t('Envelope was sent to all signers'),
      isCompleted: false,
      isActive: true,
    },
    {
      step: 'awaiting',
      title: t('Awaiting Signatures'),
      date:
        envelopeStatus?.envelopeInfo.statusChangedDateTime ||
        envelope.creationDate.getTime() + 4 * 60 * 60 * 1000,
      description: t('Waiting for all signers to complete'),
      isCompleted: false,
      isActive: completedSigners === 0 && progressPercentage < 100,
    },
  ];

  if (completedSigners > 0) {
    timelineItems.push({
      step: 'started',
      title: t('Signatures Started'),
      date:
        envelopeStatus?.envelopeInfo.statusChangedDateTime ||
        envelope.creationDate.getTime() + 24 * 60 * 60 * 1000,
      description: t('First signature completed'),
      isCompleted: false,
      isActive: completedSigners > 0 && progressPercentage < 100,
    });
  }

  if (progressPercentage === 100) {
    timelineItems.push({
      step: 'completed',
      title: t('All Signatures Complete'),
      date:
        envelopeStatus?.envelopeInfo.statusChangedDateTime ||
        envelope.creationDate.getTime() + 48 * 60 * 60 * 1000,
      description: t('All signers have completed their signatures'),
      isCompleted: true,
      isActive: false,
    });
  }

  const displayItems = invertedDirection
    ? [...timelineItems].reverse()
    : timelineItems;

  return (
    <div className="card rounded-xl">
      <div className="card-inner">
        <h6 className="card-title mb-3">{t('Envelope Timeline')}</h6>

        <div className="timeline">
          {displayItems.map((item) => (
            <EnvelopeTimelineItem
              key={`${item.step}-${item.title}`}
              step={item.step}
              title={item.title}
              date={item.date}
              description={item.description}
              isCompleted={item.isCompleted}
              isActive={item.isActive}
              uniformMarkers={uniformMarkers}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EnvelopeTimeline;
